import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShoppingCart, Star, StarOff, ZoomIn, ZoomOut, X, Bell, ImageOff, Heart, Share2 } from 'lucide-react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useMetalRates } from '../context/MetalRatesContext';
import { calculatePrice } from '../utils/priceCalculator';
import { toast } from 'sonner';
import { subscribeToStockChanges } from '../utils/stockSync';
import { ProductDetailSEO } from '../components/SEOHead';
import { trackViewItem, trackAddToWishlist } from '../utils/analytics';
import ProductSizeScale from '../components/ProductSizeScale';

export default function ProductDetailPage() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const { metalRates } = useMetalRates();
  const [reviews, setReviews] = useState([]);
  const [reviewSummary, setReviewSummary] = useState({ total_reviews: 0, average_rating: 0, rating_breakdown: {5: 0, 4: 0, 3: 0, 2: 0, 1: 0} });
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isZoomed, setIsZoomed] = useState(false);
  const [zoomPosition, setZoomPosition] = useState({ x: 50, y: 50 });
  const [showLightbox, setShowLightbox] = useState(false);
  const [isNotifyRequested, setIsNotifyRequested] = useState(false);
  const [mainImageError, setMainImageError] = useState(false);
  const [mainImageLoaded, setMainImageLoaded] = useState(false);
  const [selectedLength, setSelectedLength] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [isWishlisted, setIsWishlisted] = useState(false);
  const imageRef = useRef(null);
  const { user } = useAuth();
  const { addToCart } = useCart();

  const fetchData = useCallback(async () => {
    try {
      const [productRes, reviewsRes, summaryRes] = await Promise.all([
        api.get(`/products/${id}`),
        api.get(`/reviews/${id}`),
        api.get(`/reviews/${id}/summary`)
      ]);
      setProduct(productRes.data);
      setReviews(reviewsRes.data.reviews || []);
      setReviewSummary(summaryRes.data || { total_reviews: 0, average_rating: 0, rating_breakdown: {5: 0, 4: 0, 3: 0, 2: 0, 1: 0} });
    } catch (error) {
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Check wishlist status
  useEffect(() => {
    if (user && id) {
      api.get(`/wishlist/check/${id}`)
        .then(res => setIsWishlisted(res.data.is_wishlisted))
        .catch(() => {});
    }
  }, [user, id]);

  const toggleWishlist = async () => {
    if (!user) {
      toast.error('Please login to add to wishlist');
      return;
    }
    try {
      if (isWishlisted) {
        await api.delete(`/wishlist/${id}`);
        setIsWishlisted(false);
        toast.success('Removed from wishlist');
      } else {
        await api.post(`/wishlist/${id}`);
        setIsWishlisted(true);
        toast.success('Added to wishlist');
        if (product) trackAddToWishlist(product);
      }
    } catch (error) {
      toast.error('Failed to update wishlist');
    }
  };

  const handleShare = async () => {
    const shareUrl = window.location.href;
    const shareData = { title: product?.name || 'Product', text: `Check out ${product?.name || 'this product'} on Marissa Gold!`, url: shareUrl };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(shareUrl);
        toast.success('Link copied to clipboard!');
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        await navigator.clipboard.writeText(shareUrl);
        toast.success('Link copied to clipboard!');
      }
    }
  };

  // Subscribe to stock changes from admin pages
  useEffect(() => {
    const unsubscribe = subscribeToStockChanges((data) => {
      // Only refetch if this product was updated
      if (data.productId === id) {
        fetchData();
      }
    });
    return () => unsubscribe();
  }, [id, fetchData]);

  useEffect(() => {
    // Set default selections when product loads
    if (product) {
      if (product.length_enabled && product.length_options?.length > 0 && !selectedLength) {
        setSelectedLength(product.length_options[0]);
      }
      if (product.color_enabled && product.color_options?.length > 0 && !selectedColor) {
        setSelectedColor(product.color_options[0]);
      }
      
      // Track product view in Google Analytics
      trackViewItem(product);
    }
  }, [product, selectedLength, selectedColor]);

  // Determine availability status
  const getAvailabilityStatus = () => {
    if (!product) return { available: false, message: 'Loading...' };
    if (product.is_sold_out) return { available: false, message: 'Sold Out' };
    if (product.is_by_order) return { available: true, message: 'Available (Made to Order)' };
    // Treat undefined, null, or 0 as out of stock
    const stock = product.stock_quantity ?? 0;
    if (stock > 0) return { available: true, message: `In Stock (${stock} available)` };
    return { available: false, message: 'Out of Stock' };
  };

  const handleAddToCart = () => {
    if (!product || !priceDetails) return;
    const availability = getAvailabilityStatus();
    if (!availability.available) {
      toast.error('This product is currently not available');
      return;
    }
    // Include selected options in cart
    const productWithOptions = {
      ...product,
      selectedLength: selectedLength || null,
      selectedColor: selectedColor || null
    };
    addToCart(productWithOptions, priceDetails);
    toast.success('Added to cart!');
  };

  const handleNotifyMe = async () => {
    if (!user) {
      toast.error('Please login to get notified');
      return;
    }
    try {
      await api.post(`/products/${product.id}/notify-me`);
      setIsNotifyRequested(true);
      toast.success('You will be notified when this product is back in stock');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to subscribe for notifications');
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Please login to submit a review');
      return;
    }
    try {
      await api.post('/reviews', {
        product_id: id,
        rating,
        comment
      });
      toast.success('Review submitted!');
      setComment('');
      setRating(5);
      fetchData();
    } catch (error) {
      toast.error('Failed to submit review');
    }
  };

  if (!product) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  const priceDetails = calculatePrice(product, metalRates);
  const avgRating = reviews.length > 0
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : 0;

  const handleMouseMove = (e) => {
    if (!imageRef.current) return;
    const rect = imageRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setZoomPosition({ x, y });
  };

  // Reset image states when selected image changes
  const handleImageSelect = (index) => {
    setSelectedImageIndex(index);
    setMainImageLoaded(false);
    setMainImageError(false);
  };

  const productImages = product.images?.filter(img => img && img.trim() !== '') || [];
  const currentImage = productImages[selectedImageIndex] || productImages[0];

  return (
    <div className="min-h-screen py-12" data-testid="product-detail-page">
      {/* SEO Meta Tags */}
      {product && <ProductDetailSEO product={product} />}
      
      {/* Lightbox Modal */}
      {showLightbox && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setShowLightbox(false)}
        >
          <button
            onClick={() => setShowLightbox(false)}
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors"
            data-testid="close-lightbox"
          >
            <X className="h-8 w-8" />
          </button>
          <img
            src={currentImage}
            alt={product.name}
            className="max-w-full max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          {productImages.length > 1 && (
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
              {productImages.map((img, index) => (
                <button
                  key={`lightbox-thumb-${index}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedImageIndex(index);
                  }}
                  className={`w-16 h-16 rounded-md overflow-hidden border-2 transition-all ${
                    selectedImageIndex === index ? 'border-white' : 'border-transparent opacity-60 hover:opacity-100'
                  }`}
                >
                  <img src={img} alt={`View ${index + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:sticky lg:top-24 h-fit space-y-4"
          >
            {/* Main Image with Zoom */}
            <div 
              ref={imageRef}
              className="aspect-square bg-gray-100 rounded-lg overflow-hidden relative cursor-zoom-in group"
              onMouseEnter={() => setIsZoomed(true)}
              onMouseLeave={() => setIsZoomed(false)}
              onMouseMove={handleMouseMove}
              onClick={() => !mainImageError && setShowLightbox(true)}
              data-testid="main-image-container"
            >
              {!mainImageLoaded && !mainImageError && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                  <div className="animate-pulse w-16 h-16 bg-gray-200 rounded-full"></div>
                </div>
              )}
              {mainImageError ? (
                <div className="w-full h-full flex items-center justify-center bg-gray-100">
                  <div className="text-center text-gray-400">
                    <ImageOff className="h-16 w-16 mx-auto mb-2" />
                    <p className="text-sm">Image unavailable</p>
                  </div>
                </div>
              ) : (
                <img
                  src={currentImage}
                  alt={product.name}
                  className={`w-full h-full object-cover transition-transform duration-200 ${
                    isZoomed ? 'scale-150' : 'scale-100'
                  } ${mainImageLoaded ? 'opacity-100' : 'opacity-0'}`}
                  style={isZoomed ? {
                    transformOrigin: `${zoomPosition.x}% ${zoomPosition.y}%`
                  } : (product.image_focal_points?.[selectedImageIndex] ? {
                    objectPosition: `${product.image_focal_points[selectedImageIndex].x}% ${product.image_focal_points[selectedImageIndex].y}%`
                  } : {})}
                  onLoad={() => setMainImageLoaded(true)}
                  onError={() => setMainImageError(true)}
                  loading="eager"
                />
              )}
              <div className="absolute top-4 right-4 bg-white/80 backdrop-blur-sm rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <ZoomIn className="h-5 w-5 text-gray-700" />
              </div>
            </div>

            {/* Thumbnail Gallery */}
            {productImages.length > 1 && (
              <div className="flex space-x-3" data-testid="image-thumbnails">
                {productImages.map((img, index) => (
                  <button
                    key={`thumb-${index}`}
                    onClick={() => handleImageSelect(index)}
                    className={`w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                      selectedImageIndex === index 
                        ? 'border-[#C9A967] ring-2 ring-[#C9A967]/30' 
                        : 'border-gray-200 hover:border-gray-400'
                    }`}
                    data-testid={`thumbnail-${index}`}
                  >
                    <img
                      src={img}
                      alt={`${product.name} view ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}

            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-500">Hover to zoom | Click to view fullscreen</p>
              <div className="flex items-center gap-2">
                <button
                  onClick={toggleWishlist}
                  className={`p-2.5 rounded-full border transition-all ${
                    isWishlisted 
                      ? 'bg-red-500 text-white border-red-500' 
                      : 'bg-white text-gray-500 border-gray-300 hover:text-red-500 hover:border-red-300'
                  }`}
                  data-testid="detail-wishlist-btn"
                >
                  <Heart className={`h-5 w-5 ${isWishlisted ? 'fill-current' : ''}`} />
                </button>
                <button
                  onClick={handleShare}
                  className="p-2.5 rounded-full border border-gray-300 bg-white text-gray-500 hover:text-[#1a3a5c] hover:border-[#1a3a5c] transition-all"
                  data-testid="detail-share-btn"
                >
                  <Share2 className="h-5 w-5" />
                </button>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <div>
              <h1 className="font-serif text-4xl font-bold text-[#1a3a5c] mb-2" data-testid="product-title">
                {product.name}
              </h1>
              <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600 mb-2">
                <span className="px-3 py-1 bg-[#1a3a5c] text-white rounded-full">{product.category}</span>
                <span className="px-3 py-1 bg-gray-100 rounded-full">{product.metal_type}</span>
                {product.metal_carat && (
                  <span className="px-3 py-1 bg-[#C9A967]/20 text-[#8B7355] rounded-full font-medium">{product.metal_carat}</span>
                )}
                {product.model_category && (
                  <span className="px-3 py-1 bg-gray-100 rounded-full">{product.model_category}</span>
                )}
                <span className="px-3 py-1 bg-gray-100 rounded-full">
                  {priceDetails ? `${priceDetails.grossWeight}g` : `${product.weight}g`}
                </span>
              </div>
              {reviews.length > 0 && (
                <div className="flex items-center space-x-2 mt-2">
                  <div className="flex">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={`star-${i}`}
                        className={`h-4 w-4 ${i < Math.round(avgRating) ? 'fill-[#D4AF37] text-[#D4AF37]' : 'text-gray-300'}`}
                      />
                    ))}
                  </div>
                  <span className="text-sm text-gray-600">({avgRating} from {reviews.length} reviews)</span>
                </div>
              )}
            </div>

            {priceDetails && (
              <div className="bg-[#FDFBF7] p-6 rounded-lg border border-[#D4AF37]/20">
                <h3 className="font-serif text-xl font-semibold text-[#1a3a5c] mb-4">Price Breakup</h3>
                <table className="w-full" data-testid="price-breakup-table">
                  <tbody>
                    {/* Metal Price */}
                    <tr className="border-b border-gray-200">
                      <td className="py-2 text-gray-600">
                        Metal ({product.metal_type}{product.metal_carat ? ` ${product.metal_carat}` : ''} - {product.weight}g)
                      </td>
                      <td className="py-2 text-right font-medium">₹{priceDetails.basePrice}</td>
                    </tr>
                    
                    {/* Making Charges */}
                    <tr className="border-b border-gray-200">
                      <td className="py-2 text-gray-600">Making Charges</td>
                      <td className="py-2 text-right font-medium">₹{priceDetails.makingCharges}</td>
                    </tr>
                    
                    {/* Discount on Making Charges - only show if there's a discount */}
                    {parseFloat(priceDetails.discountPercent) > 0 && (
                      <tr className="border-b border-gray-200 bg-green-50">
                        <td className="py-2 text-green-700 font-medium">Discount ({priceDetails.discountPercent}%) on Making Charges</td>
                        <td className="py-2 text-right font-medium text-green-700">-₹{priceDetails.discountAmount}</td>
                      </tr>
                    )}
                    
                    {/* Stone */}
                    {parseFloat(priceDetails.stoneAmount) > 0 && (
                      <tr className="border-b border-gray-200">
                        <td className="py-2 text-gray-600">
                          Stone ({priceDetails.stoneWeight} gm)
                        </td>
                        <td className="py-2 text-right font-medium">₹{priceDetails.stoneAmount}</td>
                      </tr>
                    )}
                    
                    {/* Diamond */}
                    {parseFloat(priceDetails.diamondAmount) > 0 && (
                      <tr className="border-b border-gray-200">
                        <td className="py-2 text-gray-600">
                          Diamond ({priceDetails.diamondCarats} carat / {priceDetails.diamondGrams} gm)
                          {priceDetails.diamondQuality && ` - ${priceDetails.diamondQuality}`}
                        </td>
                        <td className="py-2 text-right font-medium">₹{priceDetails.diamondAmount}</td>
                      </tr>
                    )}
                    
                    {/* Precious Stone */}
                    {parseFloat(priceDetails.preciousStoneAmount) > 0 && (
                      <tr className="border-b border-gray-200">
                        <td className="py-2 text-gray-600">
                          Precious Stone ({priceDetails.preciousStoneCarats} carat / {priceDetails.preciousStoneGrams} gm)
                        </td>
                        <td className="py-2 text-right font-medium">₹{priceDetails.preciousStoneAmount}</td>
                      </tr>
                    )}
                    
                    {/* Other Material */}
                    {parseFloat(priceDetails.otherMaterialAmount) > 0 && (
                      <tr className="border-b border-gray-200">
                        <td className="py-2 text-gray-600">
                          {priceDetails.otherMaterialType || 'Other Material'} ({priceDetails.otherMaterialWeight} gm)
                        </td>
                        <td className="py-2 text-right font-medium">₹{priceDetails.otherMaterialAmount}</td>
                      </tr>
                    )}
                    
                    {/* Subtotal */}
                    <tr className="border-b border-gray-200">
                      <td className="py-2 text-gray-600">Subtotal</td>
                      <td className="py-2 text-right font-medium">₹{priceDetails.subtotal}</td>
                    </tr>
                    
                    {/* GST */}
                    <tr className="border-b border-gray-200">
                      <td className="py-2 text-gray-600">GST ({priceDetails.gstPercent}%)</td>
                      <td className="py-2 text-right font-medium">₹{priceDetails.gstAmount}</td>
                    </tr>
                    
                    {/* Total Price */}
                    <tr className="font-bold border-b border-gray-200">
                      <td className="py-3 text-[#1a3a5c] text-lg">Total Price</td>
                      <td className="py-3 text-right text-[#1a3a5c] text-2xl" data-testid="total-price">₹{priceDetails.totalPrice}</td>
                    </tr>
                    
                    {/* Net and Gross Weight */}
                    <tr className="border-b border-gray-200 bg-gray-50">
                      <td className="py-2 text-gray-600 text-sm">Net Weight (Metal only)</td>
                      <td className="py-2 text-right font-medium text-sm">{priceDetails.netWeight} gm</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="py-2 text-gray-600 text-sm">Gross Weight (Total)</td>
                      <td className="py-2 text-right font-medium text-sm">{priceDetails.grossWeight} gm</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            <div>
              <h3 className="font-serif text-xl font-semibold text-[#1a3a5c] mb-3">Description</h3>
              <p className="text-gray-600 leading-relaxed">{product.description}</p>
            </div>

            {/* Size & Scale Visualization */}
            <ProductSizeScale
              widthMm={product.dimension_width_mm}
              heightMm={product.dimension_height_mm}
              productName={product.name}
              modelCategory={product.model_category}
              productImage={productImages[0]}
            />

            {/* Length Selection */}
            {product.length_enabled && product.length_options?.length > 0 && (
              <div className="border-t border-gray-200 pt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Select Length</label>
                <select
                  value={selectedLength}
                  onChange={(e) => setSelectedLength(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c] text-gray-700"
                  data-testid="length-select"
                >
                  {product.length_options.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Color Selection */}
            {product.color_enabled && product.color_options?.length > 0 && (
              <div className="border-t border-gray-200 pt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Select Color</label>
                <select
                  value={selectedColor}
                  onChange={(e) => setSelectedColor(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c] text-gray-700"
                  data-testid="color-select"
                >
                  {product.color_options.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Availability Status */}
            {(() => {
              const availability = getAvailabilityStatus();
              return !availability.available ? (
                <div>
                  <div className="bg-red-50 border border-red-200 rounded-sm p-4 mb-4">
                    <p className="text-red-600 font-medium text-center">{availability.message}</p>
                  </div>
                  <button
                    onClick={handleNotifyMe}
                    disabled={isNotifyRequested}
                    className={`w-full py-4 rounded-sm font-serif text-lg tracking-wide transition-all duration-300 flex items-center justify-center space-x-3 shadow-lg ${
                      isNotifyRequested
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-[#C9A967] text-white hover:bg-[#B8985A]'
                    }`}
                    data-testid="notify-me-button"
                  >
                    <Bell className="h-5 w-5" />
                    <span>{isNotifyRequested ? 'You will be notified' : 'Notify Me When Available'}</span>
                  </button>
                </div>
              ) : (
                <div>
                  {product.is_by_order && (
                    <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 mb-4">
                      <p className="text-blue-700 text-sm text-center">This item is made to order</p>
                    </div>
                  )}
                  <button
                    onClick={handleAddToCart}
                    className="w-full bg-gradient-to-r from-[#1a3a5c] via-[#1a3a5c] to-[#0d2240] text-white py-4 rounded-sm font-serif text-lg tracking-wide hover:from-[#0d1e33] hover:via-[#1f4268] hover:to-[#102a4a] transition-all duration-300 flex items-center justify-center space-x-3 btn-primary shadow-lg"
                    data-testid="add-to-cart-button"
                  >
                    <ShoppingCart className="h-5 w-5" />
                    <span>Add to Cart</span>
                  </button>
                </div>
              );
            })()}

            <div className="border-t border-gray-200 pt-8">
              <h3 className="font-serif text-2xl font-semibold text-[#1a3a5c] mb-6">Customer Reviews</h3>

              {/* Rating Summary */}
              {reviewSummary.total_reviews > 0 && (
                <div className="bg-white p-6 rounded-lg border border-gray-200 mb-6" data-testid="rating-summary">
                  <div className="flex flex-col md:flex-row md:items-center md:space-x-8">
                    {/* Average Rating */}
                    <div className="text-center mb-4 md:mb-0">
                      <div className="text-5xl font-bold text-[#1a3a5c]">{reviewSummary.average_rating}</div>
                      <div className="flex justify-center my-2">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={`star-${i}`}
                            className={`h-5 w-5 ${i < Math.round(reviewSummary.average_rating) ? 'fill-[#D4AF37] text-[#D4AF37]' : 'text-gray-300'}`}
                          />
                        ))}
                      </div>
                      <div className="text-sm text-gray-600">{reviewSummary.total_reviews} reviews</div>
                    </div>
                    
                    {/* Rating Breakdown */}
                    <div className="flex-1">
                      {[5, 4, 3, 2, 1].map((star) => {
                        const count = reviewSummary.rating_breakdown[star] || 0;
                        const percentage = reviewSummary.total_reviews > 0 ? (count / reviewSummary.total_reviews) * 100 : 0;
                        return (
                          <div key={star} className="flex items-center space-x-2 mb-1">
                            <span className="text-sm text-gray-600 w-8">{star} ★</span>
                            <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-[#D4AF37] rounded-full transition-all duration-500"
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                            <span className="text-sm text-gray-500 w-8">{count}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {user && (
                <form onSubmit={handleSubmitReview} className="mb-8 bg-white p-6 rounded-lg border border-gray-200" data-testid="review-form">
                  <h4 className="font-medium text-[#1a3a5c] mb-3">Write a Review</h4>
                  <div className="mb-4">
                    <label className="block text-sm text-gray-600 mb-2">Rating</label>
                    <div className="flex space-x-2">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => setRating(star)}
                          className="focus:outline-none"
                          data-testid={`rating-${star}`}
                        >
                          <Star
                            className={`h-8 w-8 ${star <= rating ? 'fill-[#D4AF37] text-[#D4AF37]' : 'text-gray-300'}`}
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm text-gray-600 mb-2">Comment</label>
                    <textarea
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c] transition-colors"
                      rows="4"
                      placeholder="Share your experience..."
                      required
                      data-testid="review-comment"
                    />
                  </div>
                  <button
                    type="submit"
                    className="bg-[#1a3a5c] text-white px-6 py-2 rounded-sm font-serif tracking-wide hover:bg-[#1f4268] transition-all duration-300"
                    data-testid="submit-review-button"
                  >
                    Submit Review
                  </button>
                </form>
              )}

              <div className="space-y-4">
                {reviews.length === 0 ? (
                  <p className="text-gray-600" data-testid="no-reviews">No reviews yet. Be the first to review!</p>
                ) : (
                  reviews.map((review) => (
                    <div key={review.id} className="bg-white p-6 rounded-lg border border-gray-200" data-testid={`review-${review.id}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-[#1a3a5c]">{review.user_name}</span>
                        <div className="flex">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={`star-${i}`}
                              className={`h-4 w-4 ${i < review.rating ? 'fill-[#D4AF37] text-[#D4AF37]' : 'text-gray-300'}`}
                            />
                          ))}
                        </div>
                      </div>
                      <p className="text-gray-600 text-sm">{review.comment}</p>
                      <p className="text-xs text-gray-400 mt-2">
                        {new Date(review.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  ))
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}